import { getTokenValue } from '@atlaskit/tokens/get-token-value';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import type { ConstellationDotColors, ConstellationPaintStyle, TwgGraphStyle } from './types';

type RgbColor = Readonly<{
	blue: number;
	green: number;
	red: number;
}>;

const clampColorChannel = (value: number): number => Math.max(0, Math.min(255, value));

const parseHexColor = (color: string): RgbColor | null => {
	const channels = color.match(/[A-Fa-f0-9]{2}/g);
	if (!color.startsWith('#') || color.length !== 7 || channels?.length !== 3) {
		return null;
	}

	const [red, green, blue] = channels.map((channel) => Number.parseInt(channel, 16));

	return {
		red,
		green,
		blue,
	};
};

const formatRgbColor = ({ blue, green, red }: RgbColor): string =>
	`rgb(${[red, green, blue].map((channel) => Math.round(clampColorChannel(channel))).join(',')})`;

export const brightenColor = (color: string, multiplier: number): string => {
	const channels = parseHexColor(color);
	if (!channels) {
		return color;
	}

	return formatRgbColor({
		red: channels.red * multiplier,
		green: channels.green * multiplier,
		blue: channels.blue * multiplier,
	});
};

export const CONSTELLATION_COLOR_FALLBACKS: ConstellationDotColors = {
	active: ['#1868DB', '#AF59E1', '#6A9A23', '#FCA700'],
	inactive: '#6B6E76',
};

export const TWG_GRAPH_STYLE_FALLBACKS: TwgGraphStyle = {
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Canvas requires a concrete color when token CSS is unavailable.
	color: '#505258',
	edgeOpacity: 0.8,
	pulseHaloOpacity: 0.3,
};

const DARK_TWG_COLOR_FALLBACK = '#FFFFFF';
const DARK_TWG_EDGE_OPACITY = 0.75;
const DARK_TWG_PULSE_HALO_OPACITY = 0.35;

const DARK_GLOW_OPACITY = 0.58;

const resolveConstellationPaintStyle = (colorMode?: 'light' | 'dark'): ConstellationPaintStyle => ({
	dotColors: {
		active: [
			getTokenValue('color.rovo.icon.blue', CONSTELLATION_COLOR_FALLBACKS.active[0]),
			getTokenValue('color.rovo.icon.purple', CONSTELLATION_COLOR_FALLBACKS.active[1]),
			getTokenValue('color.rovo.icon.lime', CONSTELLATION_COLOR_FALLBACKS.active[2]),
			getTokenValue('color.rovo.icon.saffron', CONSTELLATION_COLOR_FALLBACKS.active[3]),
		],
		inactive: getTokenValue('color.icon.subtlest', CONSTELLATION_COLOR_FALLBACKS.inactive),
	},
	glowOpacity: colorMode === 'dark' ? DARK_GLOW_OPACITY : 0,
	twgGraph: {
		color:
			colorMode === 'dark'
				? getTokenValue('color.icon', DARK_TWG_COLOR_FALLBACK)
				: getTokenValue('color.icon.subtle', TWG_GRAPH_STYLE_FALLBACKS.color),
		edgeOpacity:
			colorMode === 'dark' ? DARK_TWG_EDGE_OPACITY : TWG_GRAPH_STYLE_FALLBACKS.edgeOpacity,
		pulseHaloOpacity:
			colorMode === 'dark'
				? DARK_TWG_PULSE_HALO_OPACITY
				: TWG_GRAPH_STYLE_FALLBACKS.pulseHaloOpacity,
	},
});

export const useConstellationPaintStyle = (): ConstellationPaintStyle => {
	const { colorMode } = useThemeObserver();

	return resolveConstellationPaintStyle(colorMode);
};
